import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import { IState } from 'models/reducers/i-state.interface';

import {
  setShowCurrencyLotteryModal,
  setShowDailyRewardsModal,
  setShowSelectModal,
  setShowSelectWalletModal,
} from 'actions/global-actions';

import Button from 'components/buttons/Button';
import { GlassBox } from 'components/glassBox/GlassBox';

import CloseIcon from 'assets/icons/daily-rewards/close-plain.svg';

const ANIMATION_DURATION = 200;

const SelectModal = () => {
  const dispatch = useDispatch();
  const isConnected = useSelector((state: IState) => Boolean(state.wallet.address));
  const [isVisible, setIsVisible] = useState(false);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => dispatch(setShowSelectModal(false)), ANIMATION_DURATION);
  }, [dispatch]);

  const handleDailyRewards = () => {
    dispatch(setShowSelectModal(false));
    const now = new Date();
    dispatch(setShowDailyRewardsModal({ year: now.getFullYear(), month: now.getMonth() + 1 }));
  };

  const handleWheelOfFortune = () => {
    dispatch(setShowSelectModal(false));
    dispatch(setShowCurrencyLotteryModal(true));
  };

  useEffect(() => {
    requestAnimationFrame(() => setIsVisible(true));
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleClose]);

  const boxClass =
    'tw-flex tw-h-[321px] tw-w-[352px] tw-items-center tw-justify-center min-[836px]:tw-h-[500px] min-[836px]:tw-w-[500px] min-[836px]:tw-flex-1';

  return (
    <div className="tw-fixed tw-inset-0 tw-z-[101] tw-flex tw-items-center tw-justify-center tw-overflow-y-auto tw-px-[10px] tw-py-[20px] tw-font-jost min-[576px]:tw-px-[26px] bp-md:tw-px-[44px] bp-2xl:tw-px-0">
      {/* Backdrop — dimmed only, no blur */}
      <div
        className={twMerge(
          'tw-fixed tw-inset-0 tw-bg-black/50 tw-transition-opacity tw-duration-200',
          isVisible ? 'tw-opacity-100' : 'tw-opacity-0'
        )}
        onClick={handleClose}
      />

      {/* Content */}
      <div
        className={twMerge(
          'tw-relative tw-flex tw-w-full tw-max-w-[1080px] tw-flex-col tw-items-center tw-transition-all tw-duration-200',
          isVisible ? 'tw-scale-100 tw-opacity-100' : 'tw-scale-95 tw-opacity-0'
        )}
      >
        {/* Title bar */}
        <div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-px-[15px] tw-pb-[40px]">
          <div className="tw-h-[24px] tw-w-[24px] tw-shrink-0" />
          <h2 className="tw-m-0 tw-text-[24px] tw-font-bold tw-text-white lg:tw-text-[40px]">Choose Event Type</h2>
          <img
            src={CloseIcon}
            alt="Close"
            onClick={handleClose}
            className="tw-block tw-h-[24px] tw-w-[24px] tw-shrink-0 tw-cursor-pointer"
          />
        </div>

        {/* Content */}
        {isConnected ? (
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-[40px] min-[836px]:tw-flex-row min-[836px]:tw-justify-center">
            <GlassBox
              gradient="linear-gradient(to bottom right, #2D5EFF 0%, rgba(45, 94, 255, 0.2) 100%)"
              borderRadius="10px"
              backgroundOpacity={0.2}
              blurAmount="20px"
              className={boxClass}
            >
              <div
                className="tw-flex tw-h-full tw-w-full tw-cursor-pointer tw-items-center tw-justify-center"
                onClick={handleDailyRewards}
              >
                <span className="tw-text-[20px] tw-font-medium tw-text-white lg:tw-text-[30px]">Daily Rewards</span>
              </div>
            </GlassBox>
            <GlassBox
              gradient="linear-gradient(to bottom right, #2D5EFF 0%, rgba(45, 94, 255, 0.2) 100%)"
              borderRadius="10px"
              backgroundOpacity={0.2}
              blurAmount="20px"
              className={boxClass}
            >
              <div
                className="tw-flex tw-h-full tw-w-full tw-cursor-pointer tw-items-center tw-justify-center"
                onClick={handleWheelOfFortune}
              >
                <span className="tw-text-[20px] tw-font-medium tw-text-white lg:tw-text-[30px]">Wheel of Fortune</span>
              </div>
            </GlassBox>
          </div>
        ) : (
          <GlassBox
            gradient="linear-gradient(to bottom right, #2D5EFF 0%, rgba(45, 94, 255, 0.2) 100%)"
            borderRadius="10px"
            backgroundOpacity={0.2}
            blurAmount="20px"
            className="tw-flex tw-w-[352px] tw-flex-col tw-items-center tw-justify-center tw-gap-[30px] tw-px-[20px] tw-py-[60px] min-[836px]:tw-w-[500px]"
          >
            <p className="tw-text-center tw-text-[18px] tw-font-medium tw-text-white lg:tw-text-[24px]">
              Please connect your wallet to participate in events.
            </p>
            <Button
              color="primary"
              size="medium"
              onClick={() => {
                dispatch(setShowSelectModal(false));
                dispatch(setShowSelectWalletModal(true));
              }}
            >
              Connect Wallet
            </Button>
          </GlassBox>
        )}
      </div>
    </div>
  );
};

export default SelectModal;
